'use client'

/**
 * Pre-redirect notice for an OAuth connect flow.
 *
 * OAuth finishes on screens we do not own. Once the browser leaves for the
 * provider we can no longer surface guidance, so anything the user needs to
 * know in order to get through *their* screens has to land here, before the
 * redirect.
 *
 * Shared by the Google and Meta notices: same shape (title, description, a
 * short list of points, cancel/confirm), different content. Adding a third
 * provider means adding a content block, not another dialog.
 */

import type { LucideIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export interface OAuthPreflightPoint {
  icon: LucideIcon
  title: string
  body: string
}

export interface OAuthPreflightDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Runs the actual OAuth start once the user acknowledges. */
  onConfirm: () => void
  title: string
  description: string
  points: OAuthPreflightPoint[]
  cancelLabel: string
  confirmLabel: string
}

export function OAuthPreflightDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  points,
  cancelLabel,
  confirmLabel,
}: OAuthPreflightDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <ul className="space-y-4">
          {points.map(point => {
            const Icon = point.icon
            return (
              <li key={point.title} className="flex gap-3">
                <Icon className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">{point.title}</p>
                  <p className="text-muted-foreground text-sm">{point.body}</p>
                </div>
              </li>
            )
          })}
        </ul>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {cancelLabel}
          </Button>
          <Button type="button" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
